<template>
  <common-header-cover-page
    v-if="article"
    :header-cover-image="article.headerCoverImage"
    :name="article.title"
  >
    <main class="container py-8 overflow-x-hidden">
      <common-breadcrumb class="mb-8 mt-xl-8 mb-xl-16" :crumbs="breadCrumbs" />
      <div class="news-detail row gx-xxl-16">
        <div
          class="col-12"
          :class="{ 'col-lg-8 me-lg-auto': recentArticles.length > 0 }"
        >
          <ul
            v-if="article.category !== 'None'"
            class="news-detail__category mb-0 fs-7 d-block overflow-hidden text-truncate"
          >
            <li class="d-inline text-rhino fw-bolder">{{ article.type }}</li>
            &nbsp;/&nbsp;
            <li class="d-inline fw-bold">
              {{ article.category }}
            </li>
          </ul>
          <h1 class="mb-2 text-uppercase">{{ article.title }}</h1>
          <div class="d-flex align-items-start border-bottom border-primary">
            <div class="fs-7 pe-4 mb-1">{{ publishedAt }}</div>
            <div class="fs-7 mb-1">
              <fa-icon :icon="faClock" class="fs-7 me-1" />
              <span v-if="readingTimeInMinutes > 1">
                {{
                  t('general/non_special_texts/reading_time', {
                    minutes: readingTimeInMinutes,
                  })
                }}
              </span>
              <span v-else>
                {{ t('general/non_special_texts/reading_time_singular') }}
              </span>
            </div>
          </div>
        </div>

        <!-- main detail column -->
        <div
          class="news-detail__main col-12 mb-8"
          :class="{ 'col-lg-8': recentArticles.length > 0 }"
        >
          <p class="fw-bold mt-6 mb-6">{{ article.teaserText }}</p>

          <strapi-article-components
            class="news-detail__content mb-8 pb-8"
            :class="{
              'news-detail__content--has-footer':
                article.footerButtons.length > 0,
            }"
            :article="article"
          />

          <div
            v-if="article.footerButtons.length > 0"
            class="mb-16 mb-lg-0 col-12 col-md-8 col-lg-12 col-xl-8"
          >
            <news-details-footer :buttons="article.footerButtons" />
          </div>
        </div>

        <!-- recent articles list -->
        <div v-if="recentArticles.length > 0" class="col-12 col-lg-4">
          <h3>{{ t('news/recent_articles') }}:</h3>
          <news-details-recent-list :articles="recentArticles" />
        </div>
      </div>
    </main>
  </common-header-cover-page>
</template>

<script lang="ts" setup>
import { faClock } from '@fortawesome/pro-solid-svg-icons';
import { readingTime } from 'reading-time-estimator';
import {
  StrapiArticleType,
  type StrapiProductContent,
} from '~/apollo/types/types';
import type { BreadCrumb } from '~/components/common/types';
import { useArticlePublishedDate } from '../features/useArticleFeatures';
import useNewsDetailData from './features/useNewsDetailData';

const props = defineProps<{
  articleType: StrapiArticleType;
}>();

// meta related
const { t, locale } = useI18n();
const route = useRoute();
const { $sentry } = useNuxtApp();
const config = useRuntimeConfig();

const articleType = toRef(props, 'articleType');
const { newsDetail } = await useNewsDetailData(articleType);

const article = computed(() => newsDetail.value?.article);
const googleNews = computed(() => newsDetail.value?.googleNews);
const recentArticles = computed(() => newsDetail.value?.recentArticles);

const localizedSlugs = computed(() => newsDetail.value.article?.localizedSlugs);
useI18nLocalizedSlugs(localizedSlugs);

const { publishedAt } = useArticlePublishedDate(article, 'long');
/**
 * Calculate reading time. For that, we sum up all text content and calculate
 * the reading time for that. This is the pure reading time, this does not
 * take into account that the user might watch an embedded youtube video or
 * stare on a slideshow image for 10 minutes.
 */
const readingTimeInMinutes = computed(() => {
  if (article.value == null) {
    return 0;
  }

  return article.value.content
    .filter((value) => value.component === 'product.content')
    .reduce(
      (minutes, component: StrapiProductContent) =>
        minutes + Math.ceil(readingTime(component.content).minutes),
      0,
    );
});

// Fill breadcrumbs with the news title
const breadCrumbs = computed(() => {
  const ret = [
    { title: t('general/menu/item/news') as string, link: 'news' },
  ] as Array<BreadCrumb>;

  if (article.value) {
    ret.push({
      title: article.value.title,
      link: `/${article.value.type.toLowerCase()}/${article.value.slug}`,
    });
  }

  return ret;
});

useHead({
  title: () => article.value.metaTitle || article.value.title,
  script: () => {
    const scripts = [];
    if (googleNews.value) {
      scripts.push({
        type: 'application/ld+json',
        innerHTML: googleNews.value,
      });
    }
    return scripts;
  },
});

useSeoMeta({
  title: () => article.value?.metaTitle,
  description: () => article.value?.metaDescription,
  ogTitle: () => article.value?.metaTitle,
  ogDescription: () => article.value?.metaDescription,
  ogImage: () => article.value?.headerCoverImage?.url,
  ogUrl: () => `${config.public.websiteUrl}${route.fullPath}`,
  ogImageAlt: () => article.value?.metaTitle,
  twitterTitle: () => article.value?.metaTitle,
  twitterDescription: () => article.value?.metaDescription,
  twitterImage: () => article.value?.headerCoverImage?.url,
});
</script>

<style lang="scss" scoped>
@import 'gportal-theme/scss/colors';
@import 'bootstrap/scss/mixins/breakpoints';
@import 'gportal-theme/scss/breakpoints';

.news-detail {
  &__content {
    border-bottom: 1px solid $primary;

    @include media-breakpoint-up(lg) {
      border-bottom: none;
    }

    &--has-footer {
      border-bottom: 1px solid $primary !important;
    }
  }
}
</style>

<style lang="scss">
.news-detail {
  &__main {
    .container {
      padding: 0;
    }

    // custom css for strapi components go here
    .media-gallery {
      &__title {
        display: none;
      }
    }

    .strapi {
      &-you-tube {
        &__title {
          display: none;
        }
      }

      &-product-content {
        hr {
          display: none;
        }
      }
    }

    .components {
      > div {
        margin-bottom: 2rem;
      }

      > div:last-child {
        margin-bottom: 0;
      }
    }

    .btn {
      margin-right: 1rem;
    }
  }
}
</style>
